export const content = `
<p>Parcările subterane sunt, adesea, spațiul cel mai neglijat dintr-o clădire de birouri sau un complex comercial. Atenția și bugetul de curățenie se duc, în mod firesc, spre zonele vizibile — recepții, birouri, spații de vânzare — în timp ce parcarea de la subsol este tratată ca o zonă strict funcțională, unde „e suficient să nu fie plină de gunoi”.</p>
<p>Problema este că o parcare subterană are cerințe de curățare fundamental diferite față de orice altă zonă a clădirii, iar neglijarea ei pe termen lung afectează atât imaginea spațiului, cât și starea reală a pardoselii.</p>

<figure style="margin: 2rem 0; text-align: center;">
  <img src="https://i.ibb.co/LdxxnsV9/Parcari.jpg" alt="Curățenie spălare parcare subterană" style="width:100%; max-height:420px; object-fit:cover; border-radius:12px; box-shadow:0 6px 20px rgba(0,0,0,0.12);" loading="lazy" />
</figure>

<div style="background-color: #f7f9fc; border-left: 4px solid #1a73e8; padding: 1.5rem; margin: 2rem 0; border-radius: 0 8px 8px 0;">
  <p style="font-weight: 700; margin-bottom: 0.8rem; color: #1d2a3a;">Cuprins articol:</p>
  <ul style="list-style-type: none; padding-left: 0; margin-bottom: 0; line-height: 1.8;">
    <li><a href="#ce-face-diferita" style="color: #1a73e8; text-decoration: none; font-weight: 600;">1. Ce face diferită curățenia unei parcări subterane</a></li>
    <li><a href="#de-ce-conteaza" style="color: #1a73e8; text-decoration: none; font-weight: 600;">2. De ce contează o parcare subterană curată</a></li>
    <li><a href="#frecventa" style="color: #1a73e8; text-decoration: none; font-weight: 600;">3. Cât de des ar trebui spălată o parcare subterană</a></li>
    <li><a href="#etape" style="color: #1a73e8; text-decoration: none; font-weight: 600;">4. Etapele unei curățenii complete</a></li>
    <li><a href="#provocari" style="color: #1a73e8; text-decoration: none; font-weight: 600;">5. Provocările specifice ale petelor de ulei și cauciuc</a></li>
    <li><a href="#echipamente" style="color: #1a73e8; text-decoration: none; font-weight: 600;">6. Ce echipamente sunt folosite</a></li>
    <li><a href="#organizare" style="color: #1a73e8; text-decoration: none; font-weight: 600;">7. Cum se organizează intervenția fără a bloca accesul</a></li>
  </ul>
</div>

<h2 id="ce-face-diferita">Ce face diferită curățenia unei parcări subterane</h2>
<p>Spre deosebire de o parcare exterioară, spălată constant de ploaie și expusă la circulație liberă a aerului, o parcare subterană este un spațiu închis, cu ventilație limitată și lumină artificială constantă. Reziduurile specifice traficului auto — ulei, unsoare, praf de frână, particule de cauciuc — se acumulează treptat pe pardoseală și nu sunt eliminate natural.</p>
<p>Această combinație de factori face ca murdăria, mirosurile și petele să meargă în adâncime și să meargă pe perioade lungi dacă nu există un program regulat de curățare mecanizată.</p>

<h2 id="de-ce-conteaza">De ce contează o parcare subterană curată</h2>
<ul>
  <li><strong>Siguranța circulației</strong> — petele de ulei sau reziduurile acumulate pot crea suprafețe alunecoase, un risc real atât pentru pietoni, cât și pentru vehicule.</li>
  <li><strong>Calitatea aerului</strong> — praful și particulele acumulate în spații cu ventilație redusă pot afecta calitatea aerului resimțită de utilizatori.</li>
  <li><strong>Durata de viață a pardoselii</strong> — uleiul și substanțele chimice lăsate netratate pot degrada, în timp, suprafața de beton sau tratamentele epoxidice aplicate pe pardoseală.</li>
  <li><strong>Imaginea generală a clădirii</strong> — o parcare curată transmite același standard de profesionalism ca zonele vizibile ale clădirii, mai ales pentru vizitatori și clienți. Servicii speciale de spălare sunt aplicabile și pentru <a href="/curatenie-fabrici-depozite-hale">hale industriale și depozite</a>.</li>
</ul>

<h2 id="frecventa">Cât de des ar trebui spălată o parcare subterană</h2>
<p>Frecvența depinde de volumul de trafic și de tipul de utilizare a spațiului.</p>

<div style="overflow-x:auto; margin: 2rem 0;">
  <table style="width:100%; border-collapse:collapse; background:#fff; border-radius:8px; overflow:hidden; box-shadow:0 2px 10px rgba(0,0,0,0.05);">
    <thead>
      <tr style="background:#1a73e8; color:#fff; text-align:left;">
        <th style="padding:12px 16px;">Tip de parcare subterană</th>
        <th style="padding:12px 16px;">Frecvență recomandată de spălare mecanizată</th>
      </tr>
    </thead>
    <tbody>
      <tr style="border-bottom:1px solid #eee;">
        <td style="padding:12px 16px; font-weight:600;">Trafic intens (clădiri de birouri mari, mall-uri / <a href="/curatenie-galerii-comerciale">galerii comerciale</a>)</td>
        <td style="padding:12px 16px;">La fiecare 4-6 săptămâni</td>
      </tr>
      <tr style="border-bottom:1px solid #eee;">
        <td style="padding:12px 16px; font-weight:600;">Trafic mediu (birouri, spații comerciale medii)</td>
        <td style="padding:12px 16px;">La fiecare 1-3 luni</td>
      </tr>
      <tr style="border-bottom:1px solid #eee;">
        <td style="padding:12px 16px; font-weight:600;">Trafic redus (rezidențial, puține locuri)</td>
        <td style="padding:12px 16px;">La fiecare 3-6 luni</td>
      </tr>
      <tr>
        <td style="padding:12px 16px; font-weight:600;">Zone cu incidente frecvente (scurgeri de ulei)</td>
        <td style="padding:12px 16px;">Intervenție punctuală imediată</td>
      </tr>
    </tbody>
  </table>
</div>

<p>📌 <strong>Notă:</strong> Măturatul regulat nu înlocuiește spălarea mecanizată periodică. Praful fin și reziduurile uleioase rămân aderente la suprafața pardoselii chiar și după un măturat riguros.</p>

<h2 id="etape">Etapele unei curățenii complete</h2>
<ol>
  <li><strong>Îndepărtarea reziduurilor solide</strong> — Măturatul mecanizat sau manual al suprafeței, pentru a elimina praful, resturile și obiectele solide.</li>
  <li><strong>Degresarea zonelor cu pete de ulei</strong> — Aplicarea unei soluții degresante specifice pe zonele cu pete vizibile de ulei sau unsoare.</li>
  <li><strong>Spălarea mecanizată sub presiune</strong> — Folosind echipamente de spălare sub presiune cu perii rotative, iar apa uzată este colectată printr-un sistem de aspirare.</li>
  <li><strong>Uscarea și verificarea finală</strong> — Zonele tratate sunt uscate mecanic, iar suprafața este verificată pentru eliminarea completă a petelor.</li>
</ol>

<h2 id="provocari">Provocările specifice ale petelor de ulei și cauciuc</h2>
<p>Petele de ulei proaspete pot fi îndepărtate aproape complet cu soluții degresante adecvate. Petele vechi, pătrunse adânc în porii betonului, pot necesita tratamente repetate.</p>
<p>Urmele de cauciuc au o compoziție diferită și necesită soluții specifice pentru îndepărtare eficientă.</p>

<h2 id="echipamente">Ce echipamente sunt folosite</h2>
<ul>
  <li><strong>Mașini de spălat pardoseli cu perii rotative</strong> — pentru suprafețe mari, cu recuperare automată a apei.</li>
  <li><strong>Echipamente de spălare sub presiune</strong> — pentru pete rezistente sau colțuri greu accesibile.</li>
  <li><strong>Soluții degresante profesionale</strong> — formulate special pentru reziduuri de ulei și derivați petrolieri.</li>
  <li><strong>Sisteme de aspirare a apei uzate</strong> — esențiale pentru a preveni scurgerea apei murdare.</li>
</ul>

<h2 id="organizare">Cum se organizează intervenția fără a bloca accesul</h2>
<p>Intervențiile se pot organiza pe zone sau la ore cu trafic redus (noaptea sau în weekend). Pentru oferte personalizate de curățare parcări, <a href="/contact-curatenie">contactează echipa Doral Cleaning</a>.</p>

<div style="background: linear-gradient(135deg, #1a73e8 0%, #0d47a1 100%); border-radius: 12px; padding: 2rem; margin: 2.5rem 0; text-align: center; color: #fff;">
  <p style="font-size: 1.15rem; font-weight: 600; margin-bottom: 0.5rem;">Cere o ofertă personalizată!</p>
  <p style="margin-bottom: 1.2rem; opacity: 0.92;">Ai nevoie de o igienizare profesională pentru parcarea subterană? Contactează-ne astăzi și primești o evaluare gratuită!</p>
  <a href="/contact-curatenie" style="background:#fff; color:#1a73e8; font-weight:700; padding:0.65rem 1.75rem; border-radius:8px; text-decoration:none; display:inline-block;">Contactează-ne</a>
</div>
`;

export const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Cât de des trebuie spălată o parcare subterană?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Pentru parcările cu trafic intens, o spălare mecanizată completă la fiecare 1-3 luni este recomandată, în completarea măturatului și a intervențiilor punctuale pentru pete de ulei sau alte scurgeri."
      }
    },
    {
      "@type": "Question",
      "name": "De ce este mai dificilă curățarea unei parcări subterane față de una la suprafață?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Ventilația redusă, iluminatul artificial constant și acumularea de reziduuri specifice traficului auto (ulei, cauciuc, praf de frână) fac din parcările subterane un spațiu cu cerințe de curățare diferite față de una exterioară."
      }
    },
    {
      "@type": "Question",
      "name": "Se pot îndepărta complet petele de ulei de pe pardoseala unei parcări subterane?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Petele proaspete pot fi îndepărtate aproape complet cu soluții degresante adecvate, însă petele vechi, pătrunse adânc în porii betonului, pot necesita tratamente repetate sau rămân parțial vizibile."
      }
    },
    {
      "@type": "Question",
      "name": "Curățarea unei parcări subterane afectează accesul auto în timpul intervenției?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "De regulă da, parțial — intervențiile mecanizate se organizează pe zone sau la ore cu trafic redus, pentru a minimiza disconfortul, dar spălarea în profunzime necesită, de obicei, oprirea temporară a accesului în zona tratată."
      }
    }
  ]
};
